import { Check, Upload } from "lucide-react";
import type { ReactNode } from "react";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      {hint && <p className="mt-0.5 text-[11px] text-muted-foreground/80">{hint}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

export function Panel({
  title,
  description,
  right,
  children,
}: {
  title: string;
  description?: string;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-bold">{title}</h4>
          {description && (
            <p className="mt-0.5 max-w-2xl text-[11px] text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function CheckChips({
  options,
  value,
  onChange,
  readOnly = false,
  labels,
}: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
  readOnly?: boolean;
  labels?: Record<string, string>;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button
            key={o}
            type="button"
            disabled={readOnly}
            onClick={() =>
              !readOnly &&
              onChange(on ? value.filter((x) => x !== o) : [...value, o])
            }
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              on
                ? "bg-accent text-primary ring-1 ring-primary/30"
                : "bg-surface-100 text-muted-foreground hover:bg-surface-200"
            } ${readOnly ? "cursor-default" : ""}`}
          >
            {on && <Check className="mr-1 inline size-3" strokeWidth={3} />}
            {labels?.[o] ?? o}
          </button>
        );
      })}
    </div>
  );
}

export function Switch({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      style={{
        backgroundColor: on ? '#00387D' : '#E2E8F0',
        width: 44,
        height: 24,
      }}
      className="relative shrink-0 rounded-full transition-colors"
    >
      <span
        style={{
          left: on ? 22 : 2,
          top: 2,
          width: 20,
          height: 20,
          backgroundColor: '#FFFFFF',
        }}
        className="pointer-events-none absolute rounded-full shadow transition-all"
      />
    </button>
  );
}

export function ToggleRow({
  label,
  hint,
  on,
  onChange,
  mono = false,
}: {
  label: string;
  hint?: string;
  on: boolean;
  onChange: (v: boolean) => void;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-2.5">
      <div className="min-w-0">
        <div className={`text-sm font-medium ${mono ? "font-mono text-[12px]" : ""}`}>
          {label}
        </div>
        {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
      </div>
      <Switch on={on} onChange={onChange} />
    </div>
  );
}

export function NumberInput({
  value,
  onChange,
  min = -1,
  max = 100,
  suffix,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-20 rounded-lg border border-border bg-white px-2 py-1.5 text-right font-mono text-xs outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
      />
      {suffix && <span className="text-[11px] text-muted-foreground">{suffix}</span>}
    </div>
  );
}

export function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

export function TabBar({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: string; label: string; badge?: string | number }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-surface-50 p-1">
      {tabs.map((t) => {
        const on = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-colors ${
              on
                ? "bg-white font-semibold text-primary shadow-sm ring-1 ring-primary/20"
                : "font-medium text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
            {t.badge !== undefined && (
              <span
                className={`rounded px-1 text-[10px] font-bold ${
                  on ? "bg-accent text-primary" : "bg-surface-200 text-muted-foreground"
                }`}
              >
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function UploadRow({
  label,
  hint,
  onFile,
  fileName,
}: {
  label: string;
  hint: string;
  onFile?: (file: File | null) => void;
  fileName?: string | null;
}) {
  return (
    <div>
      <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 px-4 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/10">
        <Upload className="size-4" />
        {fileName ? `Replace: ${fileName}` : label}
        <input
          type="file"
          accept="image/*,.svg"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0] ?? null;
            onFile?.(file);
            e.target.value = '';
          }}
        />
      </label>
      <p className="mt-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {hint}
      </p>
    </div>
  );
}

export function ColorRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <label className="relative flex size-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full ring-1 ring-border">
        <span className="absolute inset-0" style={{ backgroundColor: value }} aria-hidden />
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </label>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{label}</div>
        <div className="truncate text-[11px] uppercase tracking-wider text-muted-foreground">
          {hint}
        </div>
        <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
          {value.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
